<template>
  <motlet :options="motletOptions">
    <template #content>
      <v-row class="h-100">
        <v-col col="4">
          <v-card class="card-wrap">
            <v-card-title>{{ $t('ov.sys.title.cpu') }}</v-card-title>
            <v-card-text class="chart-area">
              <!-- eslint-disable-next-line prettier/prettier -->
              <cpu
                :values="cpu"
                :system="system"
                :items="instances"
                :formatter="formatter.cpu"
                :max="100"
                :selected-descendant.sync="selectedDescendant"
                :contextmenu="contextmenuChart"
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col col="4">
          <v-card class="card-wrap">
            <v-card-title>{{ $t('ov.sys.title.mem') }}</v-card-title>
            <v-card-text class="chart-area">
              <!-- eslint-disable-next-line prettier/prettier -->
              <memory
                :values="memory"
                :system="system"
                :items="instances"
                :formatter="formatter.memory"
                :max="100"
                :selected-descendant.sync="selectedDescendant"
                :contextmenu="contextmenuChart"
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col col="4">
          <v-card class="card-wrap">
            <v-card-title>{{ $t('ov.sys.title.swap') }}</v-card-title>
            <v-card-text class="chart-area">
              <!-- eslint-disable-next-line prettier/prettier -->
              <swap
                :values="swap"
                :system="system"
                :items="instances"
                :formatter="formatter.swap"
                :max="100"
                :selected-descendant.sync="selectedDescendant"
                :contextmenu="contextmenuChart"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
    <template #menu>
      <contextmenu :options="contextmenuParam"></contextmenu>
    </template>
  </motlet>
</template>

<script>
import { mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import instanceable from './mixins/instanceable';
import contextmenuable from './mixins/contextmenuable';
import Cpu from '../chart/BarVertical.vue';
import Memory from '../chart/BarVertical.vue';
import Swap from '../chart/BarVertical.vue';

export default {
  mixins: [instanceable, contextmenuable],
  components: {
    Cpu,
    Memory,
    Swap,
  },
  data() {
    return {
      messages: [],
      messageSequence: 0,
      values: {
        cpu: {},
        memory: {},
        swap: {},
      },
      cpu: {},
      memory: {},
      swap: {},
      formatter: {
        cpu: param => `${param.value} %`,
        memory: param => `${param.value} %`,
        // BEGIN::Modified.by.KWShin.2023.06.27.
        // 값은 % 를 보내고 formatter는 byte() 이면 안되지...
        //swap: param => bytes(param.value),
        swap: param => `${param.value} %`,
        // FINISH::Modified.by.KWShin.2023.06.27.
      },
      contextmenuUrl: '/views/detailview/abap/os',
      selectedDescendant: {},
    };
  },
  watch: {
    instances: {
      handler: 'changeInstancesHandler',
    },
  },
  computed: {
    ...mapState('common/properties', ['isPcapOs']),
    motletOptions() {
      return {
        id: 'overview/system/abap/OperationSystem',
        title: this.$t('ov.sys.title.os'),
      };
    },
  },
  methods: {
    contextmenuChart(event) {
      this.onContextmenuChart(event, this.selectedDescendant);
    },
    //BEGIN::Removed.by.KWShin.
    // 어차피 Buffered data의 마지막 건만 필요한 경우
    // 구독 시 buffer() handler를 안 보내면
    // 서버에서 Buffered data의 마지막 건만 every() handler로 보내도록 수정함.
    // wsBufferedDataHandler(key, responses, every) {
    //   // console.log('오에스 버퍼', responses);
    //   responses.forEach(response => {
    //     if (response.length) {
    //       const last = response.length ? response[response.length - 1] : { data: [], ctime: '', empty: true };
    //       every(key, last.data, last.ctime, last.empty);
    //     }
    //   });
    // },
    //FINISH::Removed.by.KWShin.
    wsDataHandler(key, response) {
      // console.log('오에스 에브리', response);
      ++this.messageSequence;

      response.forEach(res => {
        if (key.name == 'rawOsCpu' || key.name == 'pcapOsCpu') {
          if (this.isPcapOs) {
            this.values.cpu[key.instId] = Math.ceil(parseFloat(res.usrTotal) + parseFloat(res.sysTotal));
          } else {
            this.values.cpu[key.instId] = parseInt(res.usrTotal) + parseInt(res.sysTotal);
          }
        } else if (key.name == 'rawOsMem' || key.name == 'pcapOsMem') {
          if (this.isPcapOs) {
            this.values.memory[key.instId] = Math.ceil((parseInt(res.memUsed) / parseInt(res.memTotal)) * 100);
            this.values.swap[key.instId] = Math.ceil((1 - parseInt(res.swapFree) / parseInt(res.swapSize)) * 100);
          } else {
            this.values.memory[key.instId] = Math.ceil((1 - parseInt(res.freeMem) / parseInt(res.physMem)) * 100);
            this.values.swap[key.instId] = Math.ceil((1 - parseInt(res.swapFree) / parseInt(res.swapSize)) * 100);
          }
        }
      });

      // 시스템 별 취합 후 처리
      if (this.messageSequence == this.instances.length) {
        this.cpu = cloneDeep(this.values.cpu);
        this.memory = cloneDeep(this.values.memory);
        this.swap = cloneDeep(this.values.swap);
        this.messageSequence = 0;
      }
    },
    sendMessage() {
      this.messages = [
        {
          name: this.isPcapOs ? 'pcapOsCpu' : 'rawOsCpu',
          fields: ['stime', 'usrTotal', 'sysTotal'],
        },
        {
          name: this.isPcapOs ? 'pcapOsMem' : 'rawOsMem',
          fields: this.isPcapOs ? ['stime', 'memTotal', 'memUsed', 'swapFree', 'swapSize'] : ['stime', 'freeMem', 'physMem', 'swapFree', 'swapSize'],
        },
      ];

      this.$websocket.realtime.send({
        requests: this.instances.reduce(
          (acc, instance) =>
            acc.concat(
              this.messages.map(message => ({
                key: { name: message.name, sstmId: instance.systemId, instId: instance.id },
                fields: message.fields,
                empty: false,
                handler: {
                  // buffer: this.wsBufferedDataHandler,
                  every: this.wsDataHandler,
                },
              })),
            ),
          [],
        ),
      });
    },
    initialize() {
      this.messageSequence = 0;
      // value init
      Object.keys(this.values).forEach(key => {
        this.values[key] = {};
        this[key] = {};
      });
      // websoket message init
      this.wsRequestMessages = {};
    },
    changeInstancesHandler() {
      this.initialize();
      this.sendMessage();
    },
  },
};
</script>

<style scoped>
.chart-area {
  height: calc(100% - 37px);
}
</style>
